import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Maximize2,
  PanelBottom,
  PanelRight,
  PictureInPicture2,
  X,
  type LucideIcon,
} from 'lucide-react'
import type { WorkspaceLayoutMode } from '@core/store/workspaceLayoutSlice'

/** Botões de modo, pela ordem em que aparecem na barra do dock. */
const MODE_BUTTONS: {
  v: WorkspaceLayoutMode
  Icon: LucideIcon
  label: string
}[] = [
  { v: 'bottom', Icon: PanelBottom, label: 'Acoplar em baixo' },
  { v: 'side', Icon: PanelRight, label: 'Acoplar ao lado' },
  { v: 'floating', Icon: PictureInPicture2, label: 'Destacar (flutuante)' },
  { v: 'window', Icon: ExternalLink, label: 'Destacar (janela do browser)' },
  { v: 'full', Icon: Maximize2, label: 'Ecrã inteiro' },
]

export interface ModuleChromeProps {
  /** Nome legível do módulo, para o utilizador saber o que tem aberto. */
  title: string
  mode: WorkspaceLayoutMode
  setMode: (m: WorkspaceLayoutMode) => void
  /** Fecha o painel e devolve a página ao viewer. */
  onClose: () => void
  /** Só existe nos modos acoplados. */
  onCollapse?: () => void
  /** Zona por onde se arrasta o painel flutuante. */
  onDragStart?: (e: React.PointerEvent) => void
}

/**
 * Barra de título do painel do módulo, partilhada pelo dock (em baixo e
 * ao lado), pelo painel flutuante e pelo ecrã inteiro.
 *
 * Ser a MESMA barra nos três sítios é o ponto: a versão anterior só a
 * desenhava nos modos acoplados, portanto quem carregasse uma vez em
 * "ecrã inteiro" ficava sem nenhum controlo à vista e sem forma de
 * voltar ao dock, com a escolha guardada em localStorage para sempre.
 */
function ModuleChrome({
  title,
  mode,
  setMode,
  onClose,
  onCollapse,
  onDragStart,
}: ModuleChromeProps) {
  const CollapseIcon = mode === 'side' ? ChevronRight : ChevronDown
  return (
    <div
      onPointerDown={onDragStart}
      className={`flex h-7 shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-[10px] ${
        onDragStart ? 'cursor-grab select-none active:cursor-grabbing' : ''
      }`}
    >
      <span className="truncate font-medium text-[var(--color-text)]">
        {title}
      </span>
      {mode === 'full' && (
        <button
          type="button"
          onClick={() => setMode('bottom')}
          title="Voltar a acoplar o módulo por baixo do viewer 3D"
          className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
        >
          <PanelBottom size={11} />
          Acoplar ao viewer
        </button>
      )}
      <div
        className="ml-auto flex shrink-0 items-center gap-0.5"
        // Os botões vivem dentro da zona de arrasto: sem isto, carregar
        // num deles começava a arrastar o painel flutuante.
        onPointerDown={(e) => e.stopPropagation()}
      >
        {MODE_BUTTONS.map(({ v, Icon, label }) => (
          <button
            key={v}
            type="button"
            onClick={() => setMode(v)}
            title={label}
            aria-label={label}
            aria-pressed={mode === v}
            className={`flex h-5 w-5 items-center justify-center rounded ${
              mode === v
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
            }`}
          >
            <Icon size={12} />
          </button>
        ))}
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            title={`Encolher o painel para ${mode === 'side' ? 'a direita' : 'baixo'}`}
            aria-label="Encolher o painel do módulo"
            className="ml-1 flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            <CollapseIcon size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Fechar o módulo e voltar ao viewer"
          aria-label="Fechar o módulo"
          className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  )
}

/** Tira fina que fica no lugar do dock encolhido, com a seta para o
 *  reabrir. */
export function ModuleCollapsedTab({
  orientation,
  title,
  onExpand,
}: {
  orientation: 'side' | 'bottom'
  title: string
  onExpand: () => void
}) {
  if (orientation === 'side') {
    return (
      <aside className="flex w-7 shrink-0 flex-col items-center border-l border-[var(--color-border)] bg-[var(--color-panel)]">
        <button
          type="button"
          onClick={onExpand}
          title={`Reabrir o painel: ${title}`}
          aria-label="Reabrir o painel do módulo"
          className="mt-2 flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-panel-elevated)] hover:text-[var(--color-text)]"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="mt-3 [writing-mode:vertical-rl] text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
          {title}
        </div>
      </aside>
    )
  }
  return (
    <aside className="flex h-7 shrink-0 items-center justify-center border-t border-[var(--color-border)] bg-[var(--color-panel)]">
      <button
        type="button"
        onClick={onExpand}
        title={`Reabrir o painel: ${title}`}
        aria-label="Reabrir o painel do módulo"
        className="flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-panel-elevated)] hover:text-[var(--color-text)]"
      >
        <ChevronUp size={16} />
      </button>
      <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
        {title}
      </span>
    </aside>
  )
}

export default ModuleChrome
